import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiFileText,
  FiDownload,
  FiLoader,
  FiAlertCircle,
  FiInfo,
  FiUsers,
  FiSend,
  FiUserCheck,
  FiClock,
  FiTarget,
  FiRefreshCw,
  FiChevronRight,
  FiChevronDown,
  FiSearch,
  FiMapPin,
  FiAward,
  FiX,
} from 'react-icons/fi'
import { LuBanknote } from 'react-icons/lu'

// Aba "Relatório" do painel Meta Ads — consome /api/meta-ads/report.
// Cruza cada lead vindo de anúncio (Click-to-WhatsApp) com a recepção dele
// no CRM (funil, líder responsável) e a conversão (cadastro de consultoras).
// Aceita range custom de datas. Export = HTML imprimível (Salvar como PDF).

interface ReportLead {
  conversationId: string; nome: string; telefone: string
  adId: string; adName: string | null; campanha: string | null
  source: 'ctwa' | 'cta'  // ctwa = rastreado pela Meta; cta = fallback por texto exato (pré-18/05)
  primeiroContato: string; etapa: string; categoria: string
  lider: string | null; atribuidoEm: string | null
  tipoCadastro: string | null; situacaoCadastro: string
  convertido: boolean; convertidoEm: string | null; diasDesdeContato: number | null
  // Desfecho auto-relatado pela líder (null se ainda sem líder atribuída).
  desfecho: 'deuCerto' | 'tentando' | 'naoDeu' | 'semResposta' | null
  desfechoLabel: string | null; desfechoMotivo: string | null; desfechoEm: string | null
}
interface ReportAd {
  adId: string; adName: string | null; campanha: string | null
  spend: number | null; contatos: number
  lider: number; atendimento: number; novos: number; outros: number
  cadastrada: number; cpl: number | null
  isPreCtwa?: boolean
}
interface ReportData {
  success: boolean
  range: { from: string; to: string }
  attributionSince: string
  coversBeforeAttribution: boolean
  metaError: string | null
  summary: {
    contatosAtribuidos: number
    contatosCtwa: number   // com referral_ad_id (Meta direto)
    contatosCta: number    // sem referral mas com CTA exato (fallback)
    totalNovosContatos: number; pctViaAnuncio: number | null
    anunciosDistintos: number; enviadasLider: number; emAtendimento: number
    novosLeads: number; outros: number; jaCadastradas: number
    investidoTotal: number | null; cplMedio: number | null
    // Desfecho auto-relatado pelas líderes — mesma fonte do PDF do funil.
    desfecho: { deuCerto: number; tentando: number; naoDeu: number; semResposta: number }
  }
  ads: ReportAd[]; leads: ReportLead[]
  porLiderDesfecho: LeaderDesfechoRow[]
  error?: string
}
// Ranking de desfecho por líder (deu certo / tentando / não deu / sem resposta).
export interface LeaderDesfechoRow {
  lider: string; leads: number
  deuCerto: number; tentando: number; naoDeu: number; semResposta: number
  convertidos: number
}

// Performance das líderes — TODOS os leads atribuídos no período (qualquer
// origem: anúncio, orgânico, indicação). Conversão é DRACO-rigorosa: só
// conta quem virou consultora DEPOIS da atribuição (mérito da líder).
// Baldes de desfecho auto-relatado pela líder (mesma fonte do PDF do funil).
export interface DesfechoCounts { deuCerto: number; tentando: number; naoDeu: number; semResposta: number }
export interface LeaderRow {
  leaderId: string; leaderName: string; leaderCode: string | null
  distrito: string; grupo: string; cidade: string
  leads: number; convertidos: number; conversao: number
  primeiraAtribuicao: string | null; ultimaAtribuicao: string | null
  desfecho: DesfechoCounts
}
export interface DistritoRow {
  distrito: string; lideres: number; grupos: number
  leads: number; convertidos: number; conversao: number
  desfecho: DesfechoCounts
}
export interface GrupoRow {
  distrito: string; grupo: string; lideres: number
  leads: number; convertidos: number; conversao: number
  desfecho: DesfechoCounts
}
export interface LeadInLeader {
  conversationId: string; nome: string; telefone: string
  etapa: string; situacaoCadastro: string; convertido: boolean
  adId: string | null; atribuidoEm: string
  primeiroContato: string | null; cpf: string | null; cidade: string | null
  bairro?: string | null; endereco?: string | null; cep?: string | null; uf?: string | null
  desfecho: DesfechoK | null; desfechoLabel: string | null
  desfechoMotivo: string | null; desfechoEm: string | null
}
export interface LeaderPerformance {
  success: boolean
  range: { from: string; to: string }
  summary: {
    leadsAtribuidos: number; lideresAtivas: number
    distritosAtingidos: number; gruposAtingidos: number
    convertidos: number; conversao: number
    desfecho: DesfechoCounts
  }
  porDistrito: DistritoRow[]; porGrupo: GrupoRow[]; porLider: LeaderRow[]
  leadsByLeader: Record<string, LeadInLeader[]>
  error?: string
}

const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function isoOffset(days: number) {
  const d = new Date(); d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtDT(s: string | null) {
  if (!s) return '—'
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/)
  return m ? `${m[3]}/${m[2]} ${m[4]}:${m[5]}` : s
}
function fmtDia(s: string | null) {
  if (!s) return '—'
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}` : s
}
function fmtDataBR(iso?: string | null) {
  if (!iso) return '—'
  const m = iso.match(/(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}
function fmtPhone(t: string | null) {
  if (!t) return '—'
  const d = String(t).replace(/\D/g, '')
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) {
    const dd = d.slice(2, 4), rest = d.slice(4)
    if (rest.length === 9) return `+55 ${dd} ${rest.slice(0, 5)}-${rest.slice(5)}`
    if (rest.length === 8) return `+55 ${dd} ${rest.slice(0, 4)}-${rest.slice(4)}`
  }
  return '+' + d
}
const DOT: Record<string, string> = {
  lider: '#16a34a', atendimento: '#2563eb', novos: '#f59e0b', outros: '#9ca3af',
}
// Desfecho da líder — cor + símbolo (símbolo carrega a info sozinho p/ P&B).
type DesfechoK = 'deuCerto' | 'tentando' | 'naoDeu' | 'semResposta'
const DESFECHO: Record<DesfechoK, { label: string; color: string; sym: string }> = {
  deuCerto:    { label: 'Deu certo',    color: '#16a34a', sym: '✓' },
  tentando:    { label: 'Tentando',     color: '#2563eb', sym: '~' },
  naoDeu:      { label: 'Não deu',      color: '#dc2626', sym: '✗' },
  semResposta: { label: 'Sem resposta', color: '#9ca3af', sym: '·' },
}
const DESFECHO_ORDER: DesfechoK[] = ['deuCerto', 'tentando', 'naoDeu', 'semResposta']

// Resumo compacto dos baldes de desfecho (✓6 ~2 ✗1 ·8) — usado nas linhas
// agregadas (distrito/grupo/líder) da árvore das líderes.
function DesfechoMini({ d }: { d?: { deuCerto: number; tentando: number; naoDeu: number; semResposta: number } }) {
  if (!d) return <span className="text-muted-foreground/40">—</span>
  const total = d.deuCerto + d.tentando + d.naoDeu + d.semResposta
  if (!total) return <span className="text-muted-foreground/40">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums" title={DESFECHO_ORDER.map(k => `${DESFECHO[k].label}: ${d[k]}`).join(' · ')}>
      {DESFECHO_ORDER.map(k => d[k] > 0
        ? <span key={k} style={{ color: DESFECHO[k].color }}>{DESFECHO[k].sym}{d[k]}</span>
        : null)}
    </span>
  )
}
// Badge do desfecho de UM lead, com motivo+data no tooltip.
function DesfechoBadge({ le }: { le: { desfecho: DesfechoK | null; desfechoLabel: string | null; desfechoMotivo: string | null; desfechoEm: string | null } }) {
  const k: DesfechoK = le.desfecho || 'semResposta'
  const meta = DESFECHO[k]
  const tip = [le.desfechoLabel, le.desfechoMotivo, le.desfechoEm].filter(Boolean).join(' · ')
  return (
    <span className="px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ backgroundColor: meta.color + '22', color: meta.color }} title={tip || meta.label}>
      {meta.sym} {le.desfechoLabel || meta.label}
    </span>
  )
}

// Rótulo de exibição do anúncio: nome real do Meta, ou "Anúncio N" pela posição.
function buildAdLabels(ads: ReportAd[]): Record<string, string> {
  const map: Record<string, string> = {}
  ads.forEach((a, i) => { map[a.adId] = a.adName || `Anúncio ${i + 1}` })
  return map
}

// Estreita o LeaderPerformance a um escopo (distrito / grupo / líder) antes de
// gerar o PDF. Recalcula rollups e summary só com o que casa — mesma mecânica
// do useMemo `filtered` da seção on-screen. Usado pelo modal de exportação.
export function filterPerf(
  perf: LeaderPerformance,
  f: { cidade?: string; distrito?: string; grupo?: string; leaderId?: string },
): LeaderPerformance {
  const pct = (num: number, den: number) => den > 0 ? +(num / den * 100).toFixed(1) : 0
  const liders = perf.porLider.filter(l =>
    (!f.cidade || l.cidade === f.cidade) &&
    (!f.distrito || l.distrito === f.distrito) &&
    (!f.grupo || l.grupo === f.grupo) &&
    (!f.leaderId || l.leaderId === f.leaderId)
  )
  // Soma baldes de desfecho (acc += d), tolerante a rows antigas sem o campo.
  const addD = (acc: DesfechoCounts, d?: DesfechoCounts) => {
    if (!d) return; acc.deuCerto += d.deuCerto; acc.tentando += d.tentando; acc.naoDeu += d.naoDeu; acc.semResposta += d.semResposta
  }
  const novoD = (): DesfechoCounts => ({ deuCerto: 0, tentando: 0, naoDeu: 0, semResposta: 0 })
  const grupoMap = new Map<string, GrupoRow>()
  const distritoMap = new Map<string, DistritoRow & { gruposSet: Set<string> }>()
  for (const l of liders) {
    const gk = `${l.distrito}||${l.grupo}`
    if (!grupoMap.has(gk)) grupoMap.set(gk, { distrito: l.distrito, grupo: l.grupo, lideres: 0, leads: 0, convertidos: 0, conversao: 0, desfecho: novoD() })
    const g = grupoMap.get(gk)!; g.lideres++; g.leads += l.leads; g.convertidos += l.convertidos; addD(g.desfecho, l.desfecho)
    if (!distritoMap.has(l.distrito)) distritoMap.set(l.distrito, { distrito: l.distrito, lideres: 0, grupos: 0, leads: 0, convertidos: 0, conversao: 0, desfecho: novoD(), gruposSet: new Set() })
    const d = distritoMap.get(l.distrito)!; d.lideres++; d.leads += l.leads; d.convertidos += l.convertidos; d.gruposSet.add(l.grupo); addD(d.desfecho, l.desfecho)
  }
  const porGrupo = Array.from(grupoMap.values()).map(g => ({ ...g, conversao: pct(g.convertidos, g.leads) })).sort((a, b) => b.leads - a.leads)
  const porDistrito = Array.from(distritoMap.values()).map(d => ({
    distrito: d.distrito, lideres: d.lideres, grupos: d.gruposSet.size,
    leads: d.leads, convertidos: d.convertidos, conversao: pct(d.convertidos, d.leads), desfecho: d.desfecho,
  })).sort((a, b) => b.leads - a.leads)
  const leadsByLeader: Record<string, LeadInLeader[]> = {}
  for (const l of liders) if (perf.leadsByLeader[l.leaderId]) leadsByLeader[l.leaderId] = perf.leadsByLeader[l.leaderId]
  const totalLeads = liders.reduce((s, l) => s + l.leads, 0)
  const totalConv = liders.reduce((s, l) => s + l.convertidos, 0)
  const totalDesf = novoD(); for (const l of liders) addD(totalDesf, l.desfecho)
  return {
    success: true,
    range: perf.range,
    summary: {
      leadsAtribuidos: totalLeads, lideresAtivas: liders.length,
      distritosAtingidos: porDistrito.length, gruposAtingidos: porGrupo.length,
      convertidos: totalConv, conversao: pct(totalConv, totalLeads), desfecho: totalDesf,
    },
    porDistrito, porGrupo, porLider: liders, leadsByLeader,
  }
}

export function ReportView() {
  const { authenticatedFetch } = useAuth()
  const [from, setFrom] = useState(isoOffset(-30))
  const [to, setTo] = useState(isoOffset(0))
  const [data, setData] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Modal de exportação: escolhe o período antes de gerar o PDF de Meta Ads.
  // (A performance das líderes saiu daqui — agora vive no painel Líderes.)
  const [showExport, setShowExport] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [expFrom, setExpFrom] = useState(isoOffset(-30))
  const [expTo, setExpTo] = useState(isoOffset(0))

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true); setErr(null)
    try {
      const r1 = await authenticatedFetch(`/api/meta-ads/report?from=${f}&to=${t}`)
      const d1 = await r1.json()
      if (!d1 || !d1.success) { setErr(d1?.error || 'Erro ao gerar o relatório'); setData(null) }
      else setData(d1)
    } catch (e: any) {
      setErr(e?.message || 'Erro de rede'); setData(null)
    } finally { setLoading(false) }
  }, [authenticatedFetch])

  // Carrega com o range default (últimos 30 dias) na primeira montagem.
  useEffect(() => { load(from, to) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (f: string, t: string) => { setFrom(f); setTo(t); load(f, t) }

  const openExport = () => {
    setExpFrom(from); setExpTo(to)
    setShowExport(true)
  }

  const generateExport = async () => {
    setExporting(true)
    try {
      let d = data
      // Refetch só se o período do modal divergir do que já está na tela.
      if (expFrom !== from || expTo !== to) {
        const r1 = await authenticatedFetch(`/api/meta-ads/report?from=${expFrom}&to=${expTo}`)
        const j1 = await r1.json()
        d = j1 && j1.success ? j1 : null
      }
      if (!d) { alert('Sem dados de Meta Ads para o período escolhido.'); return }
      const html = buildReportHtml(d, null, { includeMetaAds: true })
      const win = window.open('', '_blank')
      if (!win) { alert('Permita pop-ups para exportar o relatório.'); return }
      win.document.write(html); win.document.close()
      setShowExport(false)
    } catch (e: any) {
      alert('Erro ao gerar o relatório: ' + (e?.message || 'falha de rede'))
    } finally { setExporting(false) }
  }

  return (
    <div className="p-4 space-y-4">
      {/* Controles de período */}
      <div className="bg-card neu-card rounded-xl border border-border p-3 flex items-center gap-2 flex-wrap">
        <FiFileText className="w-4 h-4 text-violet-500" />
        <span className="text-sm font-semibold text-foreground mr-1">Relatório anúncio → CRM</span>
        <span className="text-[11px] text-muted-foreground">De</span>
        <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)}
          className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
        <span className="text-[11px] text-muted-foreground">até</span>
        <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)}
          className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
        <button onClick={() => load(from, to)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors">
          <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Gerar
        </button>
        <div className="flex gap-1 ml-1">
          {([
            { l: '7 dias', f: isoOffset(-7), t: isoOffset(0) },
            { l: '30 dias', f: isoOffset(-30), t: isoOffset(0) },
            { l: 'Desde 17/05', f: '2026-05-17', t: isoOffset(0) },
          ]).map(p => (
            <button key={p.l} onClick={() => applyPreset(p.f, p.t)}
              className="px-2 py-1 rounded-md text-[10px] font-medium bg-secondary/60 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
              {p.l}
            </button>
          ))}
        </div>
        <button onClick={openExport} disabled={!data || loading}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-foreground text-background hover:opacity-90 disabled:opacity-40 transition-opacity"
          title="Escolha o período antes de gerar a versão imprimível">
          <FiDownload className="w-3.5 h-3.5" /> Exportar PDF
        </button>
      </div>

      {loading && !data && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
          <FiLoader className="w-6 h-6 animate-spin text-violet-500" />
          <span className="text-sm">Cruzando anúncios, CRM e cadastro...</span>
        </div>
      )}

      {err && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-5 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">Erro ao gerar o relatório</h3>
            <p className="text-xs text-muted-foreground font-mono break-all">{err}</p>
          </div>
        </div>
      )}

      {data && !loading && <ReportBody data={data} />}

      {/* Modal de exportação — recorta o escopo antes de gerar o PDF */}
      {showExport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center glass-overlay p-4"
          onClick={() => !exporting && setShowExport(false)}>
          <div className="glass-panel neu-drawer neu-forms rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <FiDownload className="w-4 h-4 text-violet-500" />
              <h3 className="text-sm font-semibold text-foreground">Exportar relatório em PDF</h3>
              <button onClick={() => setShowExport(false)} className="ml-auto text-muted-foreground hover:text-foreground" title="Fechar">
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Gera o relatório de <strong className="text-foreground">Meta Ads</strong> (origem dos contatos, investimento e
                desempenho por anúncio) no período escolhido. O relatório de performance das líderes agora fica no painel{' '}
                <strong className="text-foreground">Líderes → aba Relatório</strong>.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-16 shrink-0">Período</span>
                <input type="date" value={expFrom} max={expTo} onChange={e => setExpFrom(e.target.value)}
                  className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                <span className="text-[11px] text-muted-foreground">até</span>
                <input type="date" value={expTo} min={expFrom} onChange={e => setExpTo(e.target.value)}
                  className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
              <button onClick={() => setShowExport(false)} disabled={exporting}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium bg-secondary text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">
                Cancelar
              </button>
              <button onClick={generateExport} disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-foreground text-background hover:opacity-90 disabled:opacity-40 transition-opacity">
                {exporting ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />}
                {exporting ? 'Gerando...' : 'Gerar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Seção de cobrança das líderes (árvore hierárquica) ───────────────
// Cobre TODOS os leads atribuídos no período — orgânico, indicação e
// anúncio Meta misturados. Tabela única com 4 níveis expansíveis:
// Distrito → Grupo → Líder → Lead. Filtros aplicam a árvore inteira
// (oculta nós sem matches em qualquer descendente).
export function LeaderPerformanceSection({ data }: { data: LeaderPerformance }) {
  const s = data.summary

  const [filtroDistrito, setFiltroDistrito] = useState<string>('')
  const [filtroGrupo, setFiltroGrupo] = useState<string>('')
  const [filtroLider, setFiltroLider] = useState<string>('')
  const [filtroLead, setFiltroLead] = useState<string>('')
  const [soSemConversao, setSoSemConversao] = useState(false)
  const [openDistrito, setOpenDistrito] = useState<Set<string>>(new Set())
  const [openGrupo, setOpenGrupo] = useState<Set<string>>(new Set())
  const [openLider, setOpenLider] = useState<Set<string>>(new Set())

  const distritosList = useMemo(
    () => Array.from(new Set(data.porLider.map(l => l.distrito))).sort(),
    [data.porLider]
  )
  const gruposList = useMemo(() => {
    const filtrados = filtroDistrito
      ? data.porLider.filter(l => l.distrito === filtroDistrito)
      : data.porLider
    return Array.from(new Set(filtrados.map(l => l.grupo))).sort()
  }, [data.porLider, filtroDistrito])

  // Filtragem: começa pelos LEADS (folha) e propaga pra cima — uma líder
  // só aparece se tem ≥1 lead que bate o filtro, idem grupo e distrito.
  // Isso garante que filtrar por nome de lead drilla a árvore inteira.
  // CORREÇÃO 2026-05-28: quando filtro de lead está ativo, recalcula
  // l.leads e l.convertidos contando APENAS os leads que batem. Antes
  // a líder entrava inteira com seu total — confundia diretoria
  // ("filtrou joão mas mostra 47 leads").
  const filtered = useMemo(() => {
    const leadLc = filtroLead.trim().toLowerCase()
    const liderLc = filtroLider.trim().toLowerCase()
    const liders: LeaderRow[] = []

    const matchesLead = (le: LeadInLeader) =>
      le.nome.toLowerCase().includes(leadLc) ||
      (le.telefone || '').includes(leadLc)
    const novoD = (): DesfechoCounts => ({ deuCerto: 0, tentando: 0, naoDeu: 0, semResposta: 0 })
    const addD = (acc: DesfechoCounts, d?: DesfechoCounts) => {
      if (!d) return; acc.deuCerto += d.deuCerto; acc.tentando += d.tentando; acc.naoDeu += d.naoDeu; acc.semResposta += d.semResposta
    }
    const contaDesf = (leads: LeadInLeader[]): DesfechoCounts => {
      const c = novoD(); for (const le of leads) { if (le.desfecho) c[le.desfecho]++; else c.semResposta++ } return c
    }

    for (const l of data.porLider) {
      if (filtroDistrito && l.distrito !== filtroDistrito) continue
      if (filtroGrupo && l.grupo !== filtroGrupo) continue
      if (liderLc && !l.leaderName.toLowerCase().includes(liderLc)) continue

      // Recalcula contagens da líder se filtro de lead ativo
      let liderEfetiva: LeaderRow = l
      if (leadLc) {
        const allLeads = data.leadsByLeader[l.leaderId] || []
        const matched = allLeads.filter(matchesLead)
        if (matched.length === 0) continue
        const convsMatched = matched.filter(le => le.convertido).length
        liderEfetiva = {
          ...l,
          leads: matched.length,
          convertidos: convsMatched,
          conversao: matched.length > 0 ? +(convsMatched / matched.length * 100).toFixed(1) : 0,
          desfecho: contaDesf(matched),
        }
      }

      if (soSemConversao && liderEfetiva.convertidos > 0) continue
      liders.push(liderEfetiva)
    }

    // Reagrega distrito → grupo SÓ com as líderes que passaram
    const grupoMap = new Map<string, GrupoRow>()
    const distritoMap = new Map<string, DistritoRow & { gruposSet: Set<string> }>()
    for (const l of liders) {
      const gKey = `${l.distrito}||${l.grupo}`
      if (!grupoMap.has(gKey)) {
        grupoMap.set(gKey, { distrito: l.distrito, grupo: l.grupo, lideres: 0, leads: 0, convertidos: 0, conversao: 0, desfecho: novoD() })
      }
      const g = grupoMap.get(gKey)!
      g.lideres++; g.leads += l.leads; g.convertidos += l.convertidos; addD(g.desfecho, l.desfecho)

      if (!distritoMap.has(l.distrito)) {
        distritoMap.set(l.distrito, { distrito: l.distrito, lideres: 0, grupos: 0, leads: 0, convertidos: 0, conversao: 0, desfecho: novoD(), gruposSet: new Set() })
      }
      const d = distritoMap.get(l.distrito)!
      d.lideres++; d.leads += l.leads; d.convertidos += l.convertidos; d.gruposSet.add(l.grupo); addD(d.desfecho, l.desfecho)
    }
    for (const g of grupoMap.values()) {
      g.conversao = g.leads > 0 ? +(g.convertidos / g.leads * 100).toFixed(1) : 0
    }
    const distritos = Array.from(distritoMap.values()).map(d => ({
      distrito: d.distrito, lideres: d.lideres,
      grupos: d.gruposSet.size, leads: d.leads,
      convertidos: d.convertidos,
      conversao: d.leads > 0 ? +(d.convertidos / d.leads * 100).toFixed(1) : 0,
      desfecho: d.desfecho,
    })).sort((a, b) => b.leads - a.leads)

    return { liders, grupos: Array.from(grupoMap.values()), distritos }
  }, [data, filtroDistrito, filtroGrupo, filtroLider, filtroLead, soSemConversao])

  const totaisFiltrados = useMemo(() => ({
    leads: filtered.liders.reduce((s, l) => s + l.leads, 0),
    convertidos: filtered.liders.reduce((s, l) => s + l.convertidos, 0),
    lideres: filtered.liders.length,
  }), [filtered.liders])

  const toggle = (set: Set<string>, key: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key); else next.add(key)
    setter(next)
  }
  const expandirTudo = () => {
    setOpenDistrito(new Set(filtered.distritos.map(d => d.distrito)))
    setOpenGrupo(new Set(filtered.grupos.map(g => `${g.distrito}||${g.grupo}`)))
    setOpenLider(new Set(filtered.liders.map(l => l.leaderId)))
  }
  const fecharTudo = () => { setOpenDistrito(new Set()); setOpenGrupo(new Set()); setOpenLider(new Set()) }

  if (s.leadsAtribuidos === 0) {
    return (
      <div className="bg-card neu-card rounded-xl border border-border p-5">
        <div className="flex items-center gap-2 mb-2">
          <FiAward className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-semibold text-foreground">Performance das líderes</h3>
        </div>
        <p className="text-xs text-muted-foreground">Nenhuma atribuição de líder no período selecionado.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Aviso de escopo — esta seção escapa do filtro Meta Ads */}
      <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-3 flex items-start gap-2.5">
        <FiInfo className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Performance e cobrança das líderes</strong> cobre{' '}
          <strong className="text-foreground">TODOS</strong> os leads atribuídos a uma líder no período —
          orgânico, indicação e anúncio Meta misturados. Independente do filtro Meta Ads acima.
          Conversão = lead virou consultora no DRACO <em>após</em> a atribuição.
        </p>
      </div>

      {/* KPIs — mostram total OU valores filtrados quando filtros ativos */}
      {(() => {
        const filtrosAtivos = !!(filtroDistrito || filtroGrupo || filtroLider || filtroLead || soSemConversao)
        const distritosFilt = filtered.distritos.length
        const gruposFilt = filtered.grupos.length
        const pctFilt = totaisFiltrados.leads > 0
          ? +(totaisFiltrados.convertidos / totaisFiltrados.leads * 100).toFixed(1)
          : 0
        const kpiData = filtrosAtivos
          ? [
              { label: 'Leads (filtrado)', value: String(totaisFiltrados.leads), color: '#0f172a', icon: FiSend, hi: true },
              { label: 'Líderes (filtrado)', value: String(totaisFiltrados.lideres), color: '#16a34a', icon: FiUserCheck },
              { label: 'Distritos (filtrado)', value: String(distritosFilt), color: '#0891b2', icon: FiMapPin },
              { label: 'Grupos (filtrado)', value: String(gruposFilt), color: '#7c3aed', icon: FiTarget },
              { label: 'Conversão (filtrado)', value: `${pctFilt}% (${totaisFiltrados.convertidos})`, color: '#b45309', icon: FiAward },
            ]
          : [
              { label: 'Leads atribuídos', value: String(s.leadsAtribuidos), color: '#0f172a', icon: FiSend, hi: true },
              { label: 'Líderes ativas', value: String(s.lideresAtivas), color: '#16a34a', icon: FiUserCheck },
              { label: 'Distritos atingidos', value: String(s.distritosAtingidos), color: '#0891b2', icon: FiMapPin },
              { label: 'Grupos atingidos', value: String(s.gruposAtingidos), color: '#7c3aed', icon: FiTarget },
              { label: 'Conversão geral', value: `${s.conversao}% (${s.convertidos})`, color: '#b45309', icon: FiAward },
            ]
        return (
          <>
            {filtrosAtivos && (
              <div className="text-[10px] text-emerald-700 dark:text-emerald-400 font-medium -mb-1">
                ▸ KPIs refletem os filtros aplicados (total original: {s.leadsAtribuidos} leads · {s.lideresAtivas} líderes · {s.convertidos} conv.)
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {kpiData.map((k, i) => (
                <div key={i} className={`rounded-xl border p-3 ${k.hi ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-card border-border neu-convex'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <k.icon className="w-3 h-3" style={{ color: k.color }} />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{k.label}</span>
                  </div>
                  <div className="text-xl font-bold" style={{ color: k.color }}>{k.value}</div>
                </div>
              ))}
            </div>
          </>
        )
      })()}

      {/* Resumo do desfecho auto-relatado pelas líderes (reflete os filtros) */}
      {(() => {
        const t = filtered.liders.reduce((a, l) => {
          a.deuCerto += l.desfecho?.deuCerto || 0; a.tentando += l.desfecho?.tentando || 0
          a.naoDeu += l.desfecho?.naoDeu || 0; a.semResposta += l.desfecho?.semResposta || 0; return a
        }, { deuCerto: 0, tentando: 0, naoDeu: 0, semResposta: 0 })
        const total = t.deuCerto + t.tentando + t.naoDeu + t.semResposta
        if (total === 0) return null
        return (
          <div className="bg-card neu-card rounded-xl border border-border px-3 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5"
            title="Desfecho que as líderes relataram para os leads recebidos. Mesma fonte do relatório semanal do funil — não diverge.">
            <div className="flex items-center gap-1.5">
              <FiUserCheck className="w-4 h-4 text-violet-500" />
              <span className="text-sm font-semibold text-foreground">Desfecho das líderes</span>
              <span className="text-[10px] text-muted-foreground">({total} leads)</span>
            </div>
            {DESFECHO_ORDER.map(k => (
              <div key={k} className="flex items-baseline gap-1.5" title={`${DESFECHO[k].label}: ${t[k]} lead(s)`}>
                <span className="text-sm font-bold tabular-nums" style={{ color: DESFECHO[k].color }}>{DESFECHO[k].sym}</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{DESFECHO[k].label}</span>
                <span className="text-base font-bold tabular-nums" style={{ color: DESFECHO[k].color }}>{t[k]}</span>
              </div>
            ))}
          </div>
        )
      })()}

      {/* Árvore Distrito → Grupo → Líder → Lead */}
      <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 flex-wrap">
          <FiUserCheck className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-semibold text-foreground">Distritos → grupos → líderes → leads</h3>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={expandirTudo} className="text-[10px] px-2 py-0.5 rounded bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors">Expandir tudo</button>
            <button onClick={fecharTudo} className="text-[10px] px-2 py-0.5 rounded bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors">Fechar tudo</button>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-4 py-2.5 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap">
          <select value={filtroDistrito} onChange={e => { setFiltroDistrito(e.target.value); setFiltroGrupo('') }}
            className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-emerald-500">
            <option value="">Todos os distritos</option>
            {distritosList.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filtroGrupo} onChange={e => setFiltroGrupo(e.target.value)}
            className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-emerald-500">
            <option value="">Todos os grupos</option>
            {gruposList.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <div className="relative">
            <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <input type="text" placeholder="Buscar líder..." value={filtroLider} onChange={e => setFiltroLider(e.target.value)}
              className="text-[11px] pl-7 pr-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 w-40" />
          </div>
          <div className="relative">
            <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <input type="text" placeholder="Buscar lead (nome/tel)..." value={filtroLead} onChange={e => setFiltroLead(e.target.value)}
              className="text-[11px] pl-7 pr-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 w-48" />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer"
            title="Esconde líderes que já tiveram pelo menos 1 conversão — foca em quem precisa de cobrança">
            <input type="checkbox" checked={soSemConversao} onChange={e => setSoSemConversao(e.target.checked)} className="cursor-pointer" />
            <span>Só sem conversão</span>
          </label>
          <span className="text-[10px] text-muted-foreground ml-auto">
            {totaisFiltrados.lideres} líderes · {totaisFiltrados.leads} leads · {totaisFiltrados.convertidos} conv.
            {(filtered.liders.length !== data.porLider.length) &&
              <span className="text-emerald-600 ml-1">(filtrado de {data.porLider.length}/{s.leadsAtribuidos})</span>}
          </span>
        </div>

        <div className="overflow-auto max-h-[640px]">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-card z-10 neu-thead">
              <tr>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-left w-[40%]">Nó</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-right">Líderes</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-right">Leads</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-right">Convert.</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-right">% Conv.</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-left" title="Desfecho auto-relatado pelas líderes — ✓ deu certo · ~ tentando · ✗ não deu · · sem resposta. Mesma fonte do relatório semanal do funil.">Desfecho</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-left">Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {filtered.distritos.map(d => {
                const dOpen = openDistrito.has(d.distrito)
                const gruposDoDistrito = filtered.grupos.filter(g => g.distrito === d.distrito)
                return (
                  <React.Fragment key={`dist:${d.distrito}`}>
                    <tr onClick={() => toggle(openDistrito, d.distrito, setOpenDistrito)}
                      className="hover:bg-secondary/60 cursor-pointer bg-emerald-500/5">
                      <td className="px-3 py-2 font-semibold text-foreground border-b border-border/40">
                        {dOpen ? <FiChevronDown className="inline w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <FiChevronRight className="inline w-3.5 h-3.5 mr-1.5 text-emerald-600" />}
                        <FiMapPin className="inline w-3 h-3 mr-1 text-emerald-500" />
                        {d.distrito}
                      </td>
                      <td className="px-3 py-2 text-right text-muted-foreground border-b border-border/40 tabular-nums">{d.lideres} <span className="text-muted-foreground/50">({d.grupos}g)</span></td>
                      <td className="px-3 py-2 text-right font-bold text-foreground border-b border-border/40 tabular-nums">{d.leads}</td>
                      <td className="px-3 py-2 text-right text-amber-700 font-semibold border-b border-border/40 tabular-nums">{d.convertidos || '—'}</td>
                      <td className={`px-3 py-2 text-right font-semibold border-b border-border/40 tabular-nums ${d.conversao === 0 ? 'text-red-600' : 'text-emerald-600'}`}>{d.conversao}%</td>
                      <td className="px-3 py-2 text-[11px] border-b border-border/40"><DesfechoMini d={d.desfecho} /></td>
                      <td className="px-3 py-2 text-[10px] text-muted-foreground border-b border-border/40">{d.lideres} líder(es) · {d.grupos} grupo(s)</td>
                    </tr>
                    {dOpen && gruposDoDistrito.map(g => {
                      const gKey = `${g.distrito}||${g.grupo}`
                      const gOpen = openGrupo.has(gKey)
                      const lideresDoGrupo = filtered.liders.filter(l => l.distrito === g.distrito && l.grupo === g.grupo)
                      return (
                        <React.Fragment key={`grp:${gKey}`}>
                          <tr onClick={() => toggle(openGrupo, gKey, setOpenGrupo)}
                            className="hover:bg-secondary/40 cursor-pointer bg-violet-500/5">
                            <td className="px-3 py-1.5 pl-8 text-foreground border-b border-border/30">
                              {gOpen ? <FiChevronDown className="inline w-3 h-3 mr-1.5 text-violet-600" /> : <FiChevronRight className="inline w-3 h-3 mr-1.5 text-violet-600" />}
                              <FiTarget className="inline w-3 h-3 mr-1 text-violet-500" />
                              {g.grupo}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground border-b border-border/30 tabular-nums">{g.lideres}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-foreground border-b border-border/30 tabular-nums">{g.leads}</td>
                            <td className="px-3 py-1.5 text-right text-amber-700 font-semibold border-b border-border/30 tabular-nums">{g.convertidos || '—'}</td>
                            <td className={`px-3 py-1.5 text-right font-medium border-b border-border/30 tabular-nums ${g.conversao === 0 ? 'text-red-600' : 'text-emerald-600'}`}>{g.conversao}%</td>
                            <td className="px-3 py-1.5 text-[11px] border-b border-border/30"><DesfechoMini d={g.desfecho} /></td>
                            <td className="px-3 py-1.5 text-[10px] text-muted-foreground border-b border-border/30">{g.lideres} líder(es)</td>
                          </tr>
                          {gOpen && lideresDoGrupo.map(l => {
                            const lOpen = openLider.has(l.leaderId)
                            const leadsDaLider = (data.leadsByLeader[l.leaderId] || []).filter(le => {
                              const leadLc = filtroLead.trim().toLowerCase()
                              if (!leadLc) return true
                              return le.nome.toLowerCase().includes(leadLc) || (le.telefone || '').includes(leadLc)
                            })
                            return (
                              <React.Fragment key={`lid:${l.leaderId}`}>
                                <tr onClick={() => toggle(openLider, l.leaderId, setOpenLider)}
                                  className="hover:bg-secondary/30 cursor-pointer">
                                  <td className="px-3 py-1.5 pl-16 text-foreground border-b border-border/20">
                                    {lOpen ? <FiChevronDown className="inline w-3 h-3 mr-1.5 text-amber-600" /> : <FiChevronRight className="inline w-3 h-3 mr-1.5 text-amber-600" />}
                                    <FiUserCheck className="inline w-3 h-3 mr-1 text-amber-500" />
                                    <span className="font-medium" title={l.leaderName}>{l.leaderName}</span>
                                  </td>
                                  <td className="px-3 py-1.5 text-right border-b border-border/20">—</td>
                                  <td className="px-3 py-1.5 text-right font-semibold text-foreground border-b border-border/20 tabular-nums">{l.leads}</td>
                                  <td className="px-3 py-1.5 text-right text-amber-700 font-semibold border-b border-border/20 tabular-nums">{l.convertidos || '—'}</td>
                                  <td className={`px-3 py-1.5 text-right border-b border-border/20 tabular-nums ${l.conversao === 0 ? 'text-red-600' : 'text-emerald-600'}`}>{l.conversao}%</td>
                                  <td className="px-3 py-1.5 text-[11px] border-b border-border/20"><DesfechoMini d={l.desfecho} /></td>
                                  <td className="px-3 py-1.5 text-[10px] text-muted-foreground border-b border-border/20 whitespace-nowrap">última: {fmtDT(l.ultimaAtribuicao)}</td>
                                </tr>
                                {lOpen && leadsDaLider.map((le, idx) => (
                                  <tr key={`lead:${le.conversationId}`} className="bg-muted/30">
                                    <td className="px-3 py-1 pl-24 border-b border-border/10 text-[11px]">
                                      <span className="text-muted-foreground/70 mr-1">{idx + 1}.</span>
                                      <span className="text-foreground" title={le.nome}>{le.nome}</span>
                                    </td>
                                    <td colSpan={2} className="px-3 py-1 border-b border-border/10 text-[11px] font-mono text-muted-foreground">{fmtPhone(le.telefone)}</td>
                                    <td className="px-3 py-1 text-right border-b border-border/10 text-[11px]">
                                      <span className={`px-1.5 py-0.5 rounded-full font-medium ${le.convertido ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                                        {le.situacaoCadastro}
                                      </span>
                                    </td>
                                    <td className="px-3 py-1 text-right border-b border-border/10 text-[10px] text-muted-foreground">{le.adId ? <span className="text-blue-600" title={`Anúncio Meta · ID ${le.adId}`}>Meta</span> : <span>orgânico</span>}</td>
                                    <td className="px-3 py-1 border-b border-border/10 text-[10px]"><DesfechoBadge le={le} /></td>
                                    <td className="px-3 py-1 border-b border-border/10 text-[10px] text-muted-foreground">
                                      {(le.cidade || le.cep) && (
                                        <span className="text-foreground" title={[le.endereco, le.bairro, le.cep ? `CEP ${le.cep}` : null].filter(Boolean).join(' · ')}>
                                          <FiMapPin className="inline w-3 h-3 mr-0.5 text-rose-500 align-[-1px]" />{[le.cidade, le.uf].filter(Boolean).join('/')}{le.bairro ? ` · ${le.bairro}` : ''}<span className="text-muted-foreground/50"> · </span>
                                        </span>
                                      )}
                                      <span className="text-foreground">{le.etapa}</span>
                                      <span className="text-muted-foreground/50"> · atrib. {fmtDT(le.atribuidoEm)}</span>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </React.Fragment>
                )
              })}
              {filtered.distritos.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-xs">
                    Nenhum resultado pra esta combinação de filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Desfecho auto-relatado pelas líderes: resumo (4 baldes) + ranking por líder.
// Fonte idêntica à do PDF semanal do funil (leader_lead_followups) → não diverge.
function DesfechoSection({ data }: { data: ReportData }) {
  const [open, setOpen] = useState(false)
  const d = data.summary.desfecho
  const total = d.deuCerto + d.tentando + d.naoDeu + d.semResposta
  const lideres = data.porLiderDesfecho || []
  if (total === 0) return null
  return (
    <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
      {/* Resumo dos baldes */}
      <div className="px-3 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-b border-border">
        <div className="flex items-center gap-1.5" title="Desfecho que as líderes relataram para os leads que receberam — mesma fonte do relatório semanal do funil.">
          <FiUserCheck className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-semibold text-foreground">Desfecho das líderes</span>
          <span className="text-[10px] text-muted-foreground">({total} leads atribuídos)</span>
        </div>
        {DESFECHO_ORDER.map(k => (
          <div key={k} className="flex items-baseline gap-1.5" title={`${DESFECHO[k].label}: ${d[k]} lead(s)`}>
            <span className="text-sm font-bold tabular-nums" style={{ color: DESFECHO[k].color }}>{DESFECHO[k].sym}</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{DESFECHO[k].label}</span>
            <span className="text-base font-bold tabular-nums" style={{ color: DESFECHO[k].color }}>{d[k]}</span>
          </div>
        ))}
        {lideres.length > 0 && (
          <button onClick={() => setOpen(v => !v)}
            className="ml-auto flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:underline">
            {open ? 'Ocultar por líder' : `Por líder (${lideres.length})`}
            {open ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      {/* Ranking por líder */}
      {open && lideres.length > 0 && (
        <div className="overflow-auto max-h-[340px]">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-card z-10 neu-thead">
              <tr>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-left">Líder</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground border-b border-border text-right">Leads</th>
                {DESFECHO_ORDER.map(k => (
                  <th key={k} className="px-3 py-2 font-semibold border-b border-border text-right" style={{ color: DESFECHO[k].color }} title={DESFECHO[k].label}>
                    {DESFECHO[k].sym} {DESFECHO[k].label}
                  </th>
                ))}
                <th className="px-3 py-2 font-semibold text-amber-700 border-b border-border text-right" title="Convertidas em consultora (DRACO) após chegarem pelo anúncio">Convert.</th>
              </tr>
            </thead>
            <tbody>
              {lideres.map((l, i) => (
                <tr key={l.lider} className={`hover:bg-secondary/50 ${i % 2 ? 'bg-muted/20' : ''}`}>
                  <td className="px-3 py-1.5 font-medium text-foreground border-b border-border/30 max-w-[260px] truncate" title={l.lider}>{l.lider}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-foreground border-b border-border/30 tabular-nums">{l.leads}</td>
                  <td className="px-3 py-1.5 text-right border-b border-border/30 tabular-nums" style={{ color: DESFECHO.deuCerto.color }}>{l.deuCerto || '—'}</td>
                  <td className="px-3 py-1.5 text-right border-b border-border/30 tabular-nums" style={{ color: DESFECHO.tentando.color }}>{l.tentando || '—'}</td>
                  <td className="px-3 py-1.5 text-right border-b border-border/30 tabular-nums" style={{ color: DESFECHO.naoDeu.color }}>{l.naoDeu || '—'}</td>
                  <td className="px-3 py-1.5 text-right border-b border-border/30 tabular-nums text-muted-foreground">{l.semResposta || '—'}</td>
                  <td className="px-3 py-1.5 text-right border-b border-border/30 tabular-nums text-amber-700 font-semibold">{l.convertidos || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ReportBody({ data }: { data: ReportData }) {
  const s = data.summary
  const adLabels = buildAdLabels(data.ads)
  const [showAds, setShowAds] = useState(false) // "Desempenho por anúncio" recolhido por padrão (pedido da diretoria)

  if (s.contatosAtribuidos === 0) {
    return (
      <div className="bg-blue-500/5 border border-blue-500/30 rounded-xl p-5 flex items-start gap-3">
        <FiInfo className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-1">Nenhum lead de anúncio no período</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Nenhuma pessoa chegou via Click-to-WhatsApp entre {fmtDataBR(data.range.from)} e {fmtDataBR(data.range.to)}.
            O rastreamento de anúncio só existe a partir de {fmtDataBR(data.attributionSince)} — escolha um período a partir dessa data.
          </p>
        </div>
      </div>
    )
  }

  const kpis = [
    { label: 'Contatos via anúncio', value: String(s.contatosAtribuidos), color: '#2563eb', icon: FiUsers, hi: true },
    { label: 'Total de novos contatos', value: String(s.totalNovosContatos), color: '#0f172a', icon: FiUsers, hi: false },
    { label: 'Vieram de anúncio', value: s.pctViaAnuncio != null ? `${s.pctViaAnuncio}%` : '—', color: '#7c3aed', icon: FiTarget, hi: false },
    { label: 'Anúncios distintos', value: String(s.anunciosDistintos), color: '#0891b2', icon: FiFileText, hi: false },
    { label: 'Enviadas p/ líder', value: String(s.enviadasLider), color: '#16a34a', icon: FiSend, hi: false },
    { label: 'Em atendimento', value: String(s.emAtendimento), color: '#2563eb', icon: FiClock, hi: false },
    { label: 'Novos / aguardando', value: String(s.novosLeads), color: '#f59e0b', icon: FiInfo, hi: false },
    { label: 'Convertidas (via bot)', value: String(s.jaCadastradas), color: '#b45309', icon: FiUserCheck, hi: false },
  ]

  return (
    <div className="space-y-4">
      {/* Avisos */}
      {data.coversBeforeAttribution && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2.5">
          <FiInfo className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            O rastreamento direto de qual anúncio cada pessoa veio (CTWA) começou em{' '}
            <strong className="text-foreground">{fmtDataBR(data.attributionSince)}</strong>.
            Para o período anterior, contatos que chegaram com a frase exata do anúncio Meta
            ("Olá! Quero me tornar uma consultora Tupperware...") estão{' '}
            <strong className="text-foreground">incluídos como "Anúncio não rastreado (pré-18/05)"</strong> —
            sabemos que vieram de algum anúncio, mas não conseguimos identificar qual.
            {' '}
            <strong className="text-foreground">{s.contatosCtwa}</strong> com rastreio Meta direto ·{' '}
            <strong className="text-foreground">{s.contatosCta}</strong> via texto exato.
          </p>
        </div>
      )}
      {data.metaError && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2.5">
          <FiAlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Não foi possível buscar nome/investimento dos anúncios no Meta ({data.metaError}). O cruzamento
            com o CRM está completo — só os nomes das campanhas e o custo ficaram indisponíveis.
          </p>
        </div>
      )}

      {/* KPIs — faixa compacta de uma linha (antes eram 8 cards grandes) */}
      <div className="bg-card neu-card rounded-xl border border-border px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-1.5">
        {kpis.map((k, i) => (
          <div key={i} className={`flex items-baseline gap-1.5 ${k.hi ? 'pr-3 mr-1 border-r border-border' : ''}`} title={k.label}>
            <k.icon className="w-3 h-3 shrink-0 self-center" style={{ color: k.color }} />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{k.label}</span>
            <span className="text-base font-bold tabular-nums" style={{ color: k.color }}>{k.value}</span>
          </div>
        ))}
        {s.cplMedio != null && (
          <div className="flex items-baseline gap-1.5 ml-auto" title="Investimento total no período e custo médio por contato">
            <LuBanknote className="w-3 h-3 shrink-0 self-center text-emerald-600" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Investido</span>
            <span className="text-base font-bold tabular-nums text-emerald-600">{fmtBRL(s.investidoTotal || 0)}</span>
            <span className="text-[10px] text-muted-foreground">({fmtBRL(s.cplMedio)}/contato)</span>
          </div>
        )}
      </div>

      {/* Conversão — linha fina (detalhe do critério no tooltip) */}
      <div className="bg-card neu-card rounded-xl border border-border px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground"
        title='Critério rigoroso: o lead digitou o CPF durante a conversa e o sistema confirmou a conversão cruzando com o Oracle DRACO (CTIPO=3). Quem já era consultora antes de clicar no anúncio aparece como "Já era consultora" na tabela de leads e não entra nesta contagem.'>
        <FiUserCheck className="w-4 h-4 text-amber-700 shrink-0" />
        <span className="text-lg font-extrabold text-foreground tabular-nums">{s.jaCadastradas}</span>
        <strong className="text-foreground">convertidas via bot</strong>
        <span>
          {s.jaCadastradas === 0
            ? '— nenhum lead deste período virou consultora ainda (a medição amadurece com os dias).'
            : `— lead(s) que viraram consultora no DRACO após chegarem pelo anúncio.`}
        </span>
      </div>

      {/* Desfecho das líderes — resumo + ranking por líder (mesma fonte do PDF do funil) */}
      <DesfechoSection data={data} />

      {/* Sintético por anúncio — recolhido por padrão, abre no "Detalhes" */}
      <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
        <button onClick={() => setShowAds(v => !v)}
          className="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-secondary/40 transition-colors text-left">
          <FiTarget className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-foreground">Desempenho por anúncio</h3>
          <span className="text-[10px] text-muted-foreground">{data.ads.length} anúncios</span>
          <span className="ml-auto flex items-center gap-1 text-[11px] font-medium text-violet-600">
            {showAds ? 'Ocultar' : 'Detalhes'}
            {showAds ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
          </span>
        </button>
        {showAds && (
          <div className="overflow-auto max-h-[340px] border-t border-border">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-card z-10 neu-thead">
                <tr>
                  {['Anúncio', 'Campanha', 'Contatos', 'Líder', 'Atend.', 'Novos', 'Outros', 'Convert.', 'Investido', 'Custo/contato'].map((h, i) => (
                    <th key={h} className={`px-3 py-2 font-semibold text-muted-foreground border-b border-border ${i < 2 ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.ads.map((a, i) => (
                  <tr key={a.adId} className={`hover:bg-secondary/50 ${i % 2 ? 'bg-muted/20' : ''}`}>
                    <td className="px-3 py-1.5 font-medium text-foreground border-b border-border/30 max-w-[220px] truncate" title={`${adLabels[a.adId]} — ID ${a.adId}`}>{adLabels[a.adId]}</td>
                    <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30 max-w-[180px] truncate" title={a.campanha || ''}>{a.campanha || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-foreground border-b border-border/30 tabular-nums">{a.contatos}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-600 border-b border-border/30 tabular-nums">{a.lider || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-blue-600 border-b border-border/30 tabular-nums">{a.atendimento || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-amber-600 border-b border-border/30 tabular-nums">{a.novos || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-muted-foreground border-b border-border/30 tabular-nums">{a.outros || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-amber-700 font-semibold border-b border-border/30 tabular-nums">{a.cadastrada || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-600 border-b border-border/30 tabular-nums">{a.spend != null ? fmtBRL(a.spend) : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-indigo-600 border-b border-border/30 tabular-nums">{a.cpl != null ? fmtBRL(a.cpl) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <LuBanknote className="w-3 h-3" />
        Lista de leads (orgânico + anúncio) com cobrança por líder está na próxima seção. Conversão = telefone localizado no cadastro de consultoras (DRACO).
      </p>
    </div>
  )
}

// ─── Export: HTML imprimível (Salvar como PDF) ────────────────────────
// Estrutura print-first: cada parte começa em nova página, blocos
// líder+leads são atômicos (page-break-inside: avoid), cores são reforço
// E NÃO carregam informação sozinhas (símbolos + negrito redundantes pra
// impressão P&B). Sparkline texto-Unicode pros contatos por dia (imprime
// em qualquer impressora, sem fundo colorido).
export function buildReportHtml(
  data: ReportData,
  perf: LeaderPerformance | null,
  opts: { includeMetaAds?: boolean; scopeLabel?: string; mobile?: boolean } = {},
): string {
  const includeMetaAds = opts.includeMetaAds !== false // default: incluir (compat)
  const scopeLabel = opts.scopeLabel || ''
  // mobile: página estreita vertical (leitura no celular / impressão térmica),
  // leads em cards em vez de tabela larga. O relatório de líderes SEM Meta Ads
  // sai A4 retrato por padrão; só o relatório Meta (tabela de 10 colunas)
  // continua landscape.
  const mobile = !!opts.mobile
  const pageMax = mobile ? '460px' : includeMetaAds ? '1180px' : '800px'
  const esc = (s: any) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const adLabels = buildAdLabels(data.ads)
  const s = data.summary

  // contatos por dia
  const dias: Record<string, number> = {}
  for (const l of data.leads) { const d = fmtDia(l.primeiroContato); dias[d] = (dias[d] || 0) + 1 }
  const diaList = Object.keys(dias).sort((a, b) => {
    const pa = a.split('/'), pb = b.split('/')
    return (pa[1] + pa[0]).localeCompare(pb[1] + pb[0])
  })
  const maxDia = Math.max(1, ...Object.values(dias))

  // Sparkline Unicode: ▁▂▃▄▅▆▇█ — escala 0-1 → 0-7 → caractere.
  // Funciona em qualquer impressora (é texto, não fundo colorido).
  const SPARK_CHARS = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█']
  const sparkChar = (v: number) => {
    if (v <= 0) return SPARK_CHARS[0]
    const idx = Math.min(7, Math.round((v / maxDia) * 7))
    return SPARK_CHARS[Math.max(1, idx)]
  }

  const kpi = (v: string, r: string, cls = '') =>
    `<div class="kpi ${cls}"><div class="kpi-v">${esc(v)}</div><div class="kpi-r">${esc(r)}</div></div>`

  const synRows = data.ads.map(a => `<tr>
    <td class="b">${esc(adLabels[a.adId])}</td>
    <td>${esc(a.campanha || '—')}</td>
    <td class="num b">${a.contatos}</td>
    <td class="num">${a.lider || '—'}</td>
    <td class="num">${a.atendimento || '—'}</td>
    <td class="num">${a.novos || '—'}</td>
    <td class="num">${a.outros || '—'}</td>
    <td class="num">${a.cadastrada ? `<b>${a.cadastrada}</b>` : '—'}</td>
    <td class="num">${a.spend != null ? fmtBRL(a.spend) : '—'}</td>
    <td class="num">${a.cpl != null ? fmtBRL(a.cpl) : '—'}</td>
  </tr>`).join('')

  // Linha do tempo em texto: dd/mm  ▆▆▇█▅▃  N
  const diaTextRows = diaList.map(d => `<tr>
    <td class="mono b" style="width:70px">${esc(d)}</td>
    <td class="num" style="width:50px">${dias[d]}</td>
    <td class="spark">${sparkChar(dias[d]).repeat(Math.max(1, Math.round((dias[d] / maxDia) * 24)))}</td>
  </tr>`).join('')

  const totSpend = data.ads.reduce((x, a) => x + (a.spend || 0), 0)
  const temSpend = data.ads.some(a => a.spend != null)

  // Top-3 distritos pra capa (se perf existe e tem dados)
  const top3Distritos = perf && perf.porDistrito.length > 0
    ? perf.porDistrito.slice(0, 3).map((d, i) => `
        <div class="cover-top-item">
          <div class="cover-top-rank">${i + 1}º</div>
          <div class="cover-top-name">${esc(d.distrito)}</div>
          <div class="cover-top-stats">
            <b>${d.leads}</b> leads · <b>${d.lideres}</b> líderes ·
            ${d.convertidos > 0
              ? `<span class="ok">✓ ${d.convertidos} conv. (${d.conversao}%)</span>`
              : `<span class="zero">— 0 conv.</span>`}
          </div>
        </div>`).join('')
    : ''

  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8">
<title>Relatório CRM · Tupperware Campo Grande · ${esc(fmtDataBR(data.range.from))} a ${esc(fmtDataBR(data.range.to))}</title>
<style>
  /* Print-first CSS. @page rules controlam cabeçalho e numeração de
     páginas (Chromium/Edge/Safari recentes); legado degrada gracioso. */
  @page {
    size: ${mobile ? '116mm 232mm' : includeMetaAds ? 'A4 landscape' : 'A4'};
    margin: ${mobile ? '8mm 7mm 12mm' : '16mm 14mm 18mm'};
    @bottom-right {
      content: "Página " counter(page) " de " counter(pages);
      font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 9pt; color: #6b7280;
    }
    @bottom-left {
      content: "TWMS · Relatório CRM · ${esc(fmtDataBR(data.range.from))} a ${esc(fmtDataBR(data.range.to))}";
      font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 9pt; color: #6b7280;
    }
  }
  @page :first { @bottom-left { content: ""; } @bottom-right { content: ""; } }

  /* Força fundos coloridos a imprimir (Chrome/Edge/Safari). Algumas
     impressoras ainda ignoram; por isso as cores são reforço, NÃO
     informação única. */
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

  body {
    font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    color: #1f2937; margin: 0; padding: ${mobile ? '10px' : '24px'}; background: #f3f4f6;
    font-size: ${mobile ? '12px' : '12.5px'}; line-height: 1.45;
  }
  .page { max-width: ${pageMax}; margin: 0 auto 16px; background: #fff; padding: ${mobile ? '16px 14px' : '30px 36px'}; box-shadow: 0 1px 4px rgba(0,0,0,.10); }

  /* Page-break helpers */
  .pb-after  { page-break-after: always; break-after: page; }
  .pb-before { page-break-before: always; break-before: page; }
  .pb-avoid  { page-break-inside: avoid; break-inside: avoid; }

  /* ━━━━━ Capa ━━━━━ */
  .cover { text-align: center; padding: 50px 30px 30px; }
  .cover-logo { font-size: 14px; letter-spacing: 0.25em; color: #6b7280; text-transform: uppercase; font-weight: 600; margin-bottom: 18px; }
  .cover-title { font-size: 32px; font-weight: 800; color: #0f172a; margin: 0 0 6px; line-height: 1.15; }
  .cover-subtitle { font-size: 14px; color: #4b5563; margin: 0 0 36px; }
  .cover-period { display: inline-block; background: #0f172a; color: #fff; padding: 10px 22px; border-radius: 6px; font-size: 14px; font-weight: 600; margin-bottom: 40px; }
  .cover-period b { color: #fde047; }
  .cover-bignum {
    display: flex; justify-content: center; align-items: stretch; gap: 40px;
    margin: 0 auto 36px; max-width: 760px;
  }
  .cover-bignum-item { flex: 1; padding: 22px 14px; border-radius: 10px; border: 1px solid #e5e7eb; background: #f9fafb; }
  .cover-bignum-item.hi { background: #eff6ff; border-color: #bfdbfe; }
  .cover-bignum-item.warn { background: #fef3c7; border-color: #fcd34d; }
  .cover-bignum-v { font-size: 56px; font-weight: 800; line-height: 1; color: #0f172a; }
  .cover-bignum-item.hi .cover-bignum-v { color: #1d4ed8; }
  .cover-bignum-item.warn .cover-bignum-v { color: #92400e; }
  .cover-bignum-r { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; margin-top: 8px; font-weight: 600; }
  .cover-top { text-align: left; margin: 30px auto 0; max-width: 760px; border-top: 2px solid #e5e7eb; padding-top: 22px; }
  .cover-top-title { font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #6b7280; font-weight: 600; margin-bottom: 12px; }
  .cover-top-item { display: flex; gap: 14px; align-items: center; padding: 9px 0; border-bottom: 1px solid #f0f0f0; }
  .cover-top-rank { font-size: 22px; font-weight: 800; color: #0f172a; min-width: 36px; }
  .cover-top-name { font-size: 14px; font-weight: 600; color: #0f172a; min-width: 180px; }
  .cover-top-stats { font-size: 12px; color: #4b5563; flex: 1; }
  .cover-scope-note {
    max-width: 760px; margin: 30px auto 0; padding: 14px 18px;
    border: 1px solid #e5e7eb; border-left: 4px solid #0f172a;
    background: #f9fafb; font-size: 11.5px; color: #374151; text-align: left;
    line-height: 1.55; border-radius: 4px;
  }
  .cover-scope-note b { color: #0f172a; }
  .cover-gen { font-size: 10.5px; color: #9ca3af; margin-top: 30px; font-style: italic; }

  /* ━━━━━ Page header (corrente em cada página exceto capa) ━━━━━ */
  .page-hdr { border-bottom: 2.5px solid #0f172a; padding-bottom: 10px; margin-bottom: 18px; }
  .page-hdr h1 { font-size: 18px; margin: 0 0 2px; color: #0f172a; font-weight: 700; }
  .page-hdr .sub { color: #6b7280; font-size: 11px; margin: 0; }

  /* ━━━━━ Tipografia geral ━━━━━ */
  h2 { font-size: 14px; margin: 22px 0 10px; color: #0f172a; border-bottom: 1.5px solid #e5e7eb; padding-bottom: 5px; font-weight: 700; }
  h2 .n { display: inline-block; background: #0f172a; color: #fff; width: 20px; height: 20px; line-height: 20px; text-align: center; border-radius: 4px; font-size: 11px; margin-right: 7px; font-weight: 700; }
  h3 { font-size: 13px; margin: 16px 0 8px; color: #0f172a; font-weight: 600; }
  .sub { color: #6b7280; font-size: 12px; margin: 0 0 3px; }
  .legenda { font-size: 10.5px; color: #6b7280; margin: 4px 0 0; }
  .nota { background: #fffbeb; border: 1px solid #fde68a; border-left: 4px solid #f59e0b; padding: 10px 13px; margin: 12px 0; font-size: 11.5px; border-radius: 4px; }
  .nota b { color: #92400e; }
  .nota-info { background: #ecfdf5; border-color: #a7f3d0; border-left-color: #10b981; }
  .nota-info b { color: #065f46; }

  /* KPIs */
  .kpis { display: flex; flex-wrap: wrap; gap: 10px; margin: 12px 0; }
  .kpi { flex: 1; min-width: 120px; border: 1px solid #e5e7eb; border-radius: 6px; padding: 11px 13px; background: #f9fafb; }
  .kpi-v { font-size: 22px; font-weight: 700; color: #0f172a; line-height: 1; }
  .kpi-r { font-size: 10px; color: #6b7280; margin-top: 5px; text-transform: uppercase; letter-spacing: .03em; font-weight: 600; }
  .kpi.hi { background: #eff6ff; border-color: #bfdbfe; }
  .kpi.hi .kpi-v { color: #1d4ed8; }
  .kpi.warn { background: #fef3c7; border-color: #fcd34d; }
  .kpi.warn .kpi-v { color: #92400e; }

  /* Tabelas */
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th { background: #0f172a !important; color: #fff !important; text-align: left; padding: 6px 8px; font-weight: 600; font-size: 10px; }
  th.num { text-align: right; }
  td { padding: 5px 8px; border-bottom: 1px solid #eef0f2; vertical-align: top; }
  tr:nth-child(even) td { background: #fafbfc; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .b { font-weight: 600; }
  .dim { color: #9ca3af; }
  .ok { color: #15803d; font-weight: 600; }
  .zero { color: #b91c1c; font-weight: 700; }
  .mono { font-family: ui-monospace, "SF Mono", Consolas, monospace; font-size: 10px; }
  tfoot td { background: #eef2ff !important; font-weight: 700; border-top: 2px solid #c7d2fe; }

  /* Tags / badges */
  .tag { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 9.5px; font-weight: 600; white-space: nowrap; }
  .tag-ok   { background: #dcfce7; color: #166534; }
  .tag-no   { background: #f1f5f9; color: #64748b; }
  .tag-meta { background: #dbeafe; color: #1d4ed8; }

  /* Sparkline texto */
  .spark { font-family: ui-monospace, "SF Mono", Consolas, monospace; font-size: 14px; color: #0f172a; letter-spacing: -1px; }

  /* ━━━━━ Drill por distrito ━━━━━ */
  .distrito-banner {
    background: #ecfdf5 !important; border-left: 6px solid #10b981;
    padding: 14px 18px; margin: 0 0 16px;
    display: flex; align-items: baseline; gap: 16px;
  }
  .distrito-banner h2 { border: 0; margin: 0; font-size: 22px; color: #064e3b; }
  .distrito-banner .meta { font-size: 12px; color: #047857; }
  .distrito-banner .meta b { color: #064e3b; }

  .grupo-header {
    background: #f5f3ff !important;
    border-left: 4px solid #7c3aed;
    padding: 8px 14px; margin: 16px 0 8px; font-size: 13px;
  }
  .grupo-header b { color: #5b21b6; }
  .grupo-header .meta { color: #6d28d9; font-size: 11px; margin-left: 8px; }

  .lider-bloco { margin: 8px 0 14px; }
  .lider-card {
    border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden;
  }
  .lider-row {
    display: flex; align-items: center; gap: 12px;
    background: #fef3c7 !important; border-bottom: 1px solid #fde68a;
    padding: 8px 12px; font-size: 12px;
  }
  .lider-row .lider-nome { flex: 1; font-weight: 700; color: #0f172a; }
  .lider-row .stat { font-size: 11px; color: #4b5563; }
  .lider-row .stat b { color: #0f172a; }
  .lider-row .conv-ok { color: #15803d; font-weight: 700; }
  .lider-row .conv-zero { color: #b91c1c; font-weight: 700; }
  .lider-row .conv-zero::before { content: "▲ "; }
  .lider-row .conv-ok::before   { content: "✓ "; }

  .leads-table th { background: #fffbeb !important; color: #78350f !important; font-size: 9.5px; }
  .leads-table td { font-size: 10.5px; padding: 4px 8px; }

  footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; }

  .btns { margin: 14px 0; }
  .btns button { font: inherit; font-size: 12px; padding: 8px 16px; border-radius: 6px; border: 0; background: #0f172a; color: #fff; cursor: pointer; }

  ${mobile ? `
  /* ━━━━━ Modo mobile: coluna única, tudo empilhado, tabelas compactas ━━━━━ */
  .cover { padding: 22px 8px 14px; }
  .cover-title { font-size: 24px; }
  .cover-subtitle { margin-bottom: 22px; }
  .cover-period { margin-bottom: 22px; font-size: 12.5px; padding: 8px 16px; }
  .cover-bignum { flex-direction: column; gap: 10px; max-width: none; margin-bottom: 22px; }
  .cover-bignum-item { padding: 14px 12px; }
  .cover-bignum-v { font-size: 38px; }
  .cover-top { margin-top: 18px; padding-top: 14px; }
  .cover-top-item { flex-wrap: wrap; row-gap: 2px; }
  .cover-top-name { min-width: 0; }
  .cover-scope-note { margin-top: 18px; }
  .kpis .kpi { min-width: calc(50% - 10px); padding: 9px 10px; }
  .kpi-v { font-size: 19px; }
  .page-hdr h1 { font-size: 15px; }
  .distrito-banner { flex-direction: column; align-items: flex-start; gap: 4px; padding: 10px 12px; }
  .distrito-banner h2 { font-size: 18px; }
  .lider-row { flex-wrap: wrap; row-gap: 2px; column-gap: 10px; padding: 7px 10px; }
  .lider-row .lider-nome { flex: 1 1 100%; }
  /* Na página estreita um bloco de líder com 10+ leads NÃO cabe inteiro —
     manter o pb-avoid só cria páginas semi-vazias (e estica o header do
     grupo). Cada card de lead já evita quebrar no meio. */
  .lider-bloco, .lider-bloco.pb-avoid { break-inside: auto !important; page-break-inside: auto !important; }
  table { font-size: 9.5px; }
  th, td { padding: 4px 5px; }
  /* Cards de lead (substituem a tabela larga de 9 colunas) */
  .lead-m { padding: 7px 10px; border-bottom: 1px solid #eef0f2; font-size: 11px; break-inside: avoid; }
  .lead-m:nth-child(even) { background: #fafbfc; }
  .lead-m .l1 { display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }
  .lead-m .l2 { color: #6b7280; font-size: 10px; margin-top: 1px; }
  ` : ''}

  @media print {
    body { background: #fff; padding: 0; }
    .page { box-shadow: none; max-width: none; padding: 0; margin: 0; }
    .btns { display: none; }
    thead { display: table-header-group; }
    .lider-bloco { page-break-inside: avoid; break-inside: avoid; }
    .grupo-header { page-break-after: avoid; break-after: avoid; }
    h2 { page-break-after: avoid; break-after: avoid; }
    /* PRINT: esconde botão flutuante */
  }
</style></head><body>

<!-- ━━━━━ CAPA ━━━━━ -->
<div class="page cover pb-after">
  <div class="cover-logo">TUPPERWARE · CAMPO GRANDE</div>
  <h1 class="cover-title">Relatório CRM</h1>
  <p class="cover-subtitle">${includeMetaAds ? 'Anúncios Meta &middot; Atribuição &middot; Performance das líderes' : 'Relatório individual por líder'}</p>
  ${scopeLabel ? `<div style="margin:0 0 16px;font-size:13px;color:#5b21b6;font-weight:700">Escopo: ${esc(scopeLabel)}</div>` : ''}
  <div class="cover-period">Período: <b>${esc(fmtDataBR(data.range.from))}</b> a <b>${esc(fmtDataBR(data.range.to))}</b></div>

  <div class="cover-bignum">
    <div class="cover-bignum-item hi">
      <div class="cover-bignum-v">${perf ? perf.summary.leadsAtribuidos : s.contatosAtribuidos}</div>
      <div class="cover-bignum-r">Leads atribuídos a líder</div>
    </div>
    <div class="cover-bignum-item warn">
      <div class="cover-bignum-v">${perf ? `${perf.summary.conversao}%` : (s.contatosAtribuidos > 0 ? `${Math.round(s.jaCadastradas/s.contatosAtribuidos*100)}%` : '—')}</div>
      <div class="cover-bignum-r">Conversão para consultora ${perf ? `(${perf.summary.convertidos})` : `(${s.jaCadastradas})`}</div>
    </div>
  </div>

  ${top3Distritos ? `
  <div class="cover-top">
    <div class="cover-top-title">Top 3 distritos por volume de leads</div>
    ${top3Distritos}
  </div>
  ` : ''}

  <div class="cover-scope-note">
    ${includeMetaAds ? `<b>Escopos do relatório:</b><br>
    <b>Parte 1</b> — <i>Meta Ads</i>: cobre apenas leads que vieram de anúncio (Click-to-WhatsApp ou CTA exato). Detalha investimento, custo por contato e desempenho por anúncio.<br>
    <b>Parte 2 e 3</b> — <i>Performance das líderes</i>: cobre <b>TODOS</b> os leads atribuídos a uma líder no período — orgânico, indicação e anúncio Meta misturados. Os números da capa acima referem-se a este escopo amplo.`
    : `<b>Relatório individual por líder.</b> Lista de leads atribuídos no período recortada pelo escopo escolhido — colunas <i>1º contato, CPF, nome, telefone, localização e data de encaminhamento</i>. A localização (cidade, bairro e CEP) é resolvida a partir do CEP que o lead informou ao bot; quando não temos o CEP, cai pra cidade do bot/cadastro DRACO e pode ficar em branco.`}
  </div>

  <div class="cover-gen">Gerado em ${esc(fmtDataBR(isoOffset(0)))} pelo CRM TWMS · cruzamento Meta Ads × WhatsApp × DRACO</div>

  <div class="btns" style="margin-top:30px"><button onclick="window.print()">Imprimir / Salvar como PDF</button></div>
</div>

${includeMetaAds ? `
<!-- ━━━━━ PARTE 1: Meta Ads (origem) ━━━━━ -->
<div class="page pb-after">
  <div class="page-hdr">
    <h1>Parte 1 — Meta Ads: origem dos contatos</h1>
    <p class="sub">Pessoas que chegaram via Click-to-WhatsApp e desempenho de cada anúncio.</p>
  </div>

  ${data.coversBeforeAttribution ? `<div class="nota"><b>Cobertura:</b> o rastreamento direto de qual anúncio cada pessoa veio (CTWA) começou em
    <b>${esc(fmtDataBR(data.attributionSince))}</b>. Para o período anterior, contatos com a frase exata do anúncio Meta
    ("Olá! Quero me tornar uma consultora Tupperware...") <b>estão incluídos</b> agrupados como "Anúncio não rastreado (pré-18/05)" —
    sabemos que vieram de algum anúncio, mas não identificamos qual.
    <b>${esc(String(s.contatosCtwa))}</b> com rastreio direto · <b>${esc(String(s.contatosCta))}</b> via texto exato.</div>` : ''}

  <h2><span class="n">1</span>Resumo Meta Ads</h2>
  <div class="kpis">
    ${kpi(String(s.contatosAtribuidos), 'Vieram de anúncio', 'hi')}
    ${kpi(String(s.totalNovosContatos), 'Total de novos contatos')}
    ${kpi(s.pctViaAnuncio != null ? s.pctViaAnuncio + '%' : '—', '% via anúncio')}
    ${kpi(String(s.anunciosDistintos), 'Anúncios distintos')}
    ${kpi(String(s.jaCadastradas), 'Convertidas via bot', 'warn')}
  </div>
  ${temSpend ? `
    <p class="legenda">
      Investimento total no período: <b>${esc(fmtBRL(totSpend))}</b> &middot;
      ${s.cplMedio != null ? `Custo médio por contato: <b>${esc(fmtBRL(s.cplMedio))}</b>` : 'Custo por contato indisponível.'}
    </p>` : ''}

  <h2><span class="n">2</span>Contatos por dia</h2>
  <p class="legenda">Cada bloco ▆ representa o volume relativo do dia (escala automática). Imprime em qualquer impressora.</p>
  <table class="pb-avoid"><thead><tr><th style="width:75px">Dia</th><th class="num" style="width:60px">Contatos</th><th>Volume</th></tr></thead>
  <tbody>${diaTextRows}</tbody></table>

  <h2><span class="n">3</span>Desempenho por anúncio</h2>
  <table><thead><tr>
    <th>Anúncio</th><th>Campanha</th><th class="num">Contatos</th>
    <th class="num">Líder</th><th class="num">Atend.</th><th class="num">Novos</th><th class="num">Outros</th>
    <th class="num">Convert.</th><th class="num">Investido</th><th class="num">Custo/contato</th>
  </tr></thead><tbody>${synRows}</tbody>
  <tfoot><tr>
    <td colspan="2">TOTAL — ${data.ads.length} anúncios</td>
    <td class="num">${s.contatosAtribuidos}</td><td class="num">${s.enviadasLider}</td>
    <td class="num">${s.emAtendimento}</td><td class="num">${s.novosLeads}</td><td class="num">${s.outros}</td>
    <td class="num"><b>${s.jaCadastradas}</b></td><td class="num">${temSpend ? esc(fmtBRL(totSpend)) : '—'}</td>
    <td class="num">${s.cplMedio != null ? esc(fmtBRL(s.cplMedio)) : '—'}</td>
  </tr></tfoot></table>
</div>
` : ''}

${perf && perf.summary.leadsAtribuidos > 0 ? buildLeaderPerformanceHtml(perf, esc, kpi, mobile) : ''}

<footer style="max-width:${pageMax};margin:0 auto;padding:18px 36px;text-align:center">
  Relatório gerado pelo CRM TWMS — cruzamento de mensagens WhatsApp × anúncios Meta (Click-to-WhatsApp) × funil do CRM × cadastro DRACO.
  ${includeMetaAds && data.attributionSince ? `Atribuição CTWA disponível desde ${esc(fmtDataBR(data.attributionSince))}.` : ''}
  Conversão = telefone localizado no cadastro de consultoras com data posterior à atribuição.
</footer>

</body></html>`
}

// Capítulos de Performance das líderes — Parte 2 (resumo macro) +
// Parte 3 (drill por distrito, 1 distrito por página). Cada bloco
// líder+leads é atômico (page-break-inside: avoid via .lider-bloco).
function buildLeaderPerformanceHtml(
  perf: LeaderPerformance,
  esc: (s: any) => string,
  kpi: (v: string, r: string, cls?: string) => string,
  mobile = false,
): string {
  const s = perf.summary

  // Desfecho auto-relatado pelas líderes — símbolo + cor (redundantes p/ P&B).
  const DESF: Record<string, { l: string; c: string; sym: string }> = {
    deuCerto: { l: 'Deu certo', c: '#15803d', sym: '✓' },
    tentando: { l: 'Tentando', c: '#1d4ed8', sym: '~' },
    naoDeu: { l: 'Não deu', c: '#b91c1c', sym: '✗' },
    semResposta: { l: 'Sem resposta', c: '#6b7280', sym: '·' },
  }
  const DORDER = ['deuCerto', 'tentando', 'naoDeu', 'semResposta'] as const
  // Resumo compacto "✓6 ~2 ✗1 ·8" pros nós agregados.
  const desfResumo = (d?: DesfechoCounts) => {
    if (!d) return '—'
    const parts = DORDER.filter(k => d[k] > 0).map(k => `<b style="color:${DESF[k].c}">${DESF[k].sym}${d[k]}</b>`)
    return parts.length ? parts.join(' ') : '—'
  }

  const distritoRows = perf.porDistrito.map(d => `<tr>
    <td class="b">${esc(d.distrito)}</td>
    <td class="num">${d.lideres}</td>
    <td class="num">${d.grupos}</td>
    <td class="num b">${d.leads}</td>
    <td class="num">${d.convertidos || '—'}</td>
    <td class="num ${d.conversao === 0 ? 'zero' : 'ok'}">${d.conversao}%</td>
    <td>${desfResumo(d.desfecho)}</td>
  </tr>`).join('')

  // Status visual da líder: ✓ pros convertidos (verde), ▲ alerta pros 0%
  // (vermelho) — símbolo + cor, redundantes pra leitura P&B.
  const statusLider = (l: LeaderRow) => l.convertidos > 0
    ? `<span class="conv-ok">${l.conversao}%</span>`
    : `<span class="conv-zero">0%</span>`

  // Bloco completo de uma líder + leads dela (page-break-inside avoid)
  const buildLiderBloco = (l: LeaderRow) => {
    const leads = perf.leadsByLeader[l.leaderId] || []
    // Colunas pedidas pela diretoria (substituem a planilha manual):
    // 1º Contato · CPF · Nome · Telefone · Cidade · Encaminhado. Líder e
    // Grupo ficam no cabeçalho do bloco (é agrupado por líder). Situação
    // mantida à direita por ser útil pra cobrança.
    const leadRows = leads.map((le, i) => {
      const dk = le.desfecho || 'semResposta'
      const dm = DESF[dk]
      const desfTip = [le.desfechoMotivo, le.desfechoEm].filter(Boolean).join(' · ')
      return `<tr>
      <td class="dim num" style="width:22px">${i + 1}</td>
      <td class="mono" style="width:78px">${esc(le.primeiroContato ? fmtDataBR(le.primeiroContato) : '—')}</td>
      <td class="mono" style="width:108px">${esc(le.cpf || '—')}</td>
      <td class="b" style="min-width:150px">${esc(le.nome)}</td>
      <td class="mono" style="width:120px">${esc(fmtPhone(le.telefone))}</td>
      <td style="min-width:120px">${(() => {
        const cidUf = [le.cidade, le.uf].filter(Boolean).join('/')
        const sub = [le.bairro, le.cep ? `CEP ${le.cep}` : null].filter(Boolean).join(' · ')
        if (!cidUf && !sub) return '—'
        return `${esc(cidUf || '—')}${sub ? `<div class="dim" style="font-size:9px">${esc(sub)}</div>` : ''}`
      })()}</td>
      <td class="mono" style="width:78px">${esc(le.atribuidoEm ? fmtDataBR(le.atribuidoEm) : '—')}</td>
      <td style="white-space:nowrap"><b style="color:${dm.c}">${dm.sym} ${esc(le.desfechoLabel || dm.l)}</b>${desfTip ? `<div class="dim" style="font-size:9px">${esc(desfTip)}</div>` : ''}</td>
      <td>${le.convertido
        ? `<span class="tag tag-ok">✓ ${esc(le.situacaoCadastro)}</span>`
        : `<span class="tag tag-no">${esc(le.situacaoCadastro)}</span>`}</td>
    </tr>`
    }).join('')

    // Modo mobile: card compacto por lead (a tabela de 9 colunas não cabe em
    // ~440px). Mesmos dados, empilhados.
    const leadCardsMobile = leads.map((le, i) => {
      const dk = le.desfecho || 'semResposta'
      const dm = DESF[dk]
      const cidUf = [le.cidade, le.uf].filter(Boolean).join('/')
      const loc = [cidUf, le.bairro, le.cep ? `CEP ${le.cep}` : null].filter(Boolean).join(' · ')
      const desfTip = [le.desfechoMotivo, le.desfechoEm].filter(Boolean).join(' · ')
      return `<div class="lead-m">
        <div class="l1"><b>${i + 1}. ${esc(le.nome)}</b><b style="color:${dm.c};white-space:nowrap">${dm.sym} ${esc(le.desfechoLabel || dm.l)}</b></div>
        <div class="l2">${esc(fmtPhone(le.telefone))} · CPF ${esc(le.cpf || '—')}</div>
        ${loc ? `<div class="l2">${esc(loc)}</div>` : ''}
        <div class="l2">1º contato ${esc(le.primeiroContato ? fmtDataBR(le.primeiroContato) : '—')} · encaminhado ${esc(le.atribuidoEm ? fmtDataBR(le.atribuidoEm) : '—')} · ${le.convertido ? `✓ ${esc(le.situacaoCadastro)}` : esc(le.situacaoCadastro)}</div>
        ${desfTip ? `<div class="l2" style="font-style:italic">${esc(desfTip)}</div>` : ''}
      </div>`
    }).join('')

    return `<div class="lider-bloco pb-avoid">
      <div class="lider-card">
        <div class="lider-row">
          <div class="lider-nome">${esc(l.leaderName)}</div>
          <div class="stat">Grupo <b>${esc(l.grupo)}</b></div>
          <div class="stat"><b>${l.leads}</b> leads recebidos</div>
          <div class="stat">Convertidos: ${statusLider(l)}${l.convertidos > 0 ? ` <b>(${l.convertidos})</b>` : ''}</div>
          <div class="stat">Desfecho: ${desfResumo(l.desfecho)}</div>
          <div class="stat dim">última atrib.: ${esc(l.ultimaAtribuicao || '—')}</div>
        </div>
        ${leads.length > 0 ? (mobile ? `<div>${leadCardsMobile}</div>` : `
        <table class="leads-table">
          <thead><tr>
            <th class="num" style="width:22px">#</th>
            <th>1º Contato</th><th>CPF</th><th>Nome</th><th>Telefone</th>
            <th>Localização</th><th>Encaminhado</th><th>Desfecho</th><th>Situação</th>
          </tr></thead>
          <tbody>${leadRows}</tbody>
        </table>`) : '<div style="padding:8px 14px;color:#9ca3af;font-size:11px;font-style:italic">Sem leads no detalhe (atribuição registrada sem mensagens vinculadas).</div>'}
      </div>
    </div>`
  }

  // Agrupa líderes por distrito → grupo pra renderizar drill organizado.
  // Map<distrito, Map<grupo, LeaderRow[]>>
  const arvore = new Map<string, Map<string, LeaderRow[]>>()
  for (const l of perf.porLider) {
    if (!arvore.has(l.distrito)) arvore.set(l.distrito, new Map())
    const g = arvore.get(l.distrito)!
    if (!g.has(l.grupo)) g.set(l.grupo, [])
    g.get(l.grupo)!.push(l)
  }

  // Cada distrito vira sua própria página. Header banner + grupos + líderes.
  const distritoPaginas = perf.porDistrito.map((d, idx) => {
    const gruposDoDistrito = arvore.get(d.distrito) || new Map()
    const gruposOrdenados = Array.from(gruposDoDistrito.entries())
      .sort(([, lidersA], [, lidersB]) =>
        lidersB.reduce((s, l) => s + l.leads, 0) - lidersA.reduce((s, l) => s + l.leads, 0)
      )

    const gruposHtml = gruposOrdenados.map(([grupo, liders]) => {
      const totLeadsGrupo = liders.reduce((s, l) => s + l.leads, 0)
      const totConvGrupo = liders.reduce((s, l) => s + l.convertidos, 0)
      const pctConvGrupo = totLeadsGrupo > 0 ? +(totConvGrupo / totLeadsGrupo * 100).toFixed(1) : 0

      // Sem pb-avoid no wrapper do grupo: tentar caber o grupo INTEIRO numa
      // página empurrava tudo pra próxima e deixava páginas semi-vazias
      // (ajuste 2026-07-10). O pb-avoid fica só no bloco de cada líder.
      return `<div>
        <div class="grupo-header">
          <b>Grupo ${esc(grupo)}</b>
          <span class="meta">· ${liders.length} líder(es) · ${totLeadsGrupo} leads ·
            ${totConvGrupo > 0
              ? `<b style="color:#15803d">✓ ${totConvGrupo} conv. (${pctConvGrupo}%)</b>`
              : `<b style="color:#b91c1c">▲ 0 conv.</b>`}
            · Desfecho: ${desfResumo(liders.reduce((a, l) => {
              a.deuCerto += l.desfecho?.deuCerto || 0; a.tentando += l.desfecho?.tentando || 0
              a.naoDeu += l.desfecho?.naoDeu || 0; a.semResposta += l.desfecho?.semResposta || 0; return a
            }, { deuCerto: 0, tentando: 0, naoDeu: 0, semResposta: 0 }))}
          </span>
        </div>
        ${liders.map(buildLiderBloco).join('')}
      </div>`
    }).join('')

    return `<div class="page ${idx < perf.porDistrito.length - 1 ? 'pb-after' : ''}">
      <div class="page-hdr">
        <h1>Parte 3 — Drill por distrito (${idx + 1}/${perf.porDistrito.length})</h1>
        <p class="sub">Performance e cobrança · ${esc(d.distrito)}</p>
      </div>
      <div class="distrito-banner">
        <h2>${esc(d.distrito)}</h2>
        <div class="meta">
          <b>${d.leads}</b> leads · <b>${d.lideres}</b> líderes · <b>${d.grupos}</b> grupo(s) ·
          ${d.convertidos > 0
            ? `<b class="ok">✓ ${d.convertidos} conv. (${d.conversao}%)</b>`
            : `<b class="zero">▲ 0 conv. (0%)</b>`}
        </div>
      </div>
      ${gruposHtml}
    </div>`
  }).join('')

  return `
<!-- ━━━━━ PARTE 2: Performance das líderes (macro) ━━━━━ -->
<div class="page pb-after">
  <div class="page-hdr">
    <h1>Parte 2 — Performance das líderes (visão macro)</h1>
    <p class="sub">Cobre TODOS os leads atribuídos no período — orgânico + indicação + anúncio Meta.</p>
  </div>

  <div class="nota nota-info">
    <b>Escopo:</b> esta seção cobre <b>TODOS</b> os leads atribuídos a uma líder no período.
    Independente do filtro Meta Ads da Parte 1. Conversão = lead virou consultora no DRACO
    <i>após</i> a atribuição à líder (não conta quem já era consultora antes).
    Linhas com <b style="color:#15803d">✓ verde</b> = líder com conversão;
    <b style="color:#b91c1c">▲ vermelho</b> = sem conversão (alvo de cobrança).
  </div>

  <h2><span class="n">4</span>Resumo</h2>
  <div class="kpis">
    ${kpi(String(s.leadsAtribuidos), 'Leads atribuídos', 'hi')}
    ${kpi(String(s.lideresAtivas), 'Líderes que receberam')}
    ${kpi(String(s.distritosAtingidos), 'Distritos')}
    ${kpi(String(s.gruposAtingidos), 'Grupos')}
    ${kpi(`${s.conversao}% (${s.convertidos})`, 'Conversão geral', 'warn')}
  </div>

  <p class="legenda" style="margin-top:8px">
    <b>Desfecho das líderes</b> (auto-relatado, mesma fonte do relatório semanal do funil):
    ${desfResumo(s.desfecho)} &nbsp;—&nbsp;
    <b style="color:#15803d">✓</b> deu certo ·
    <b style="color:#1d4ed8">~</b> tentando ·
    <b style="color:#b91c1c">✗</b> não deu ·
    <b style="color:#6b7280">·</b> sem resposta.
  </p>

  <h2><span class="n">5</span>Por distrito</h2>
  <p class="legenda">Visão macro — o detalhe lead-a-lead vem na Parte 3 (1 página por distrito).</p>
  <table><thead><tr>
    <th>Distrito</th><th class="num">Líderes</th><th class="num">Grupos</th>
    <th class="num">Leads</th><th class="num">Convertidos</th><th class="num">% conversão</th><th>Desfecho</th>
  </tr></thead><tbody>${distritoRows}</tbody></table>
</div>

<!-- ━━━━━ PARTE 3: Drill por distrito (1 página cada) ━━━━━ -->
${distritoPaginas}
  `
}
